import {
  type AviatorOrder,
  type IncompleteOrder,
  type Order,
  type OrderTrackingResponse,
  type OrderEngagement,
  type LastOrder,
  type OrdersHistory,
  type BusinessOrderHistory,
} from '@/features/shop/services/Orders/types'
import { apiGet } from '@/features/shared/utils/dataFetching'
import { type QueryFunctionContext } from '@tanstack/react-query'
import {
  type OrderTrackingQueryKey,
  type ActiveOrdersQueryKey,
  type OrderEngagementQueryKey,
  type BusinessOrderHistoryQueryKey,
} from '@/features/shop/services/Orders/queries'
import { getOrderKey } from '@/features/shop/services/Orders/keys'

export const fetchActiveOrders = (context: QueryFunctionContext<ActiveOrdersQueryKey>) => {
  const [, activeGroupId] = context.queryKey
  const params = activeGroupId ? `?customer_group_id=${encodeURIComponent(activeGroupId)}` : ''

  return apiGet<AviatorOrder[]>({
    config: { url: `aviator/v2/orders/active.json${params}` },
    context,
    fetcherName: 'fetchActiveOrders',
  })
}

export const fetchOrder = async (
  context: QueryFunctionContext<ReturnType<typeof getOrderKey>>,
): Promise<Order> => {
  const [, orderId, ingestType] = context.queryKey

  return apiGet<Order>({
    config: {
      url: `member-web-bff/v1/order/${orderId}?ingest_type=${ingestType}`,
    },
    context,
    options: {
      ignoredMessages: ['Forbidden', 'platform_account_mismatch'],
    },
    fetcherName: 'fetchOrderDetails',
  })
}

export const fetchOrdersHistory = (context: QueryFunctionContext) => {
  return apiGet<OrdersHistory>({
    config: {
      url: '/member-web-bff/v1/order/history?include_platform_orders=true',
    },
    context,
    fetcherName: 'fetchOrdersHistory',
  })
}

export const fetchLastDeliveredOrder = async (context: QueryFunctionContext) => {
  return apiGet<LastOrder>({
    config: {
      url: 'member-web-bff/v1/order/last',
    },
    context,
    fetcherName: 'fetchLastDeliveredOrder',
  })
}

export const fetchOrderTracking = async (context: QueryFunctionContext<OrderTrackingQueryKey>) => {
  const [, orderId] = context.queryKey

  const response = await apiGet<OrderTrackingResponse>({
    config: { url: `member-web-bff/v1/order/track/${orderId}` },
    context,
    fetcherName: 'fetchOrderTracking',
  })

  return response
}

export const fetchOrderEngagement = async (
  context: QueryFunctionContext<OrderEngagementQueryKey>,
) => {
  const orderId = context.queryKey[1].orderId
  const orderEngagement = await apiGet<OrderEngagement>({
    config: { url: `/member-web-bff/v1/orders/shopper-eta/${orderId}` },
    context,
    options: { ignoredMessages: ['order ETA is not available'] },
    fetcherName: 'fetchOrderEngagement',
  })

  // if we receive a "delivered" order status from the api, the UI will refresh, but the
  // api returns "delivered" and an undefined "future_route" (empty array). We have to
  // explicitly refetch the active order data so the map gets removed, otherwise we will
  // render the map error banner when the api returns a delivered order status.
  if (orderEngagement.order_status === 'delivered') {
    context.client.invalidateQueries({
      queryKey: getOrderKey(orderId, 'marketplace'),
    })
  }

  return orderEngagement
}

export const fetchIncompleteOrders = async (
  context: QueryFunctionContext,
): Promise<IncompleteOrder[]> => {
  return apiGet<IncompleteOrder[]>({
    config: { url: '/member-web-bff/v1/orders/incomplete' },
    context,
    fetcherName: 'fetchIncompleteOrders',
  })
}

export const fetchBusinessOrderHistory = (
  context: QueryFunctionContext<BusinessOrderHistoryQueryKey>,
): Promise<BusinessOrderHistory> => {
  const [, { startDate, endDate }] = context.queryKey
  return apiGet<BusinessOrderHistory>({
    config: {
      url: '/member-web-bff/v1/order/business-history',
      params: { start_date: startDate, end_date: endDate },
    },
    context,
    fetcherName: 'fetchBusinessOrderHistory',
  })
}
